import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Progress } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import * as authService from "../../../services/auth/authService";
import locale from "@aligov/global-locale";
import {
  UserOutlined,
  LockOutlined,
  LockFilled,
} from "@ant-design/icons";

import "../index.css";

const ResetPwdPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { isAuthenticated, user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // 初始化时设置多语言
  useEffect(() => {
    // const lang = getCookie("x-hng");
    const { lang } = locale.getLocale();

    if (lang) {
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage("en-US"); // 默认语言
    } 
  }, []);

  const urlLoginId = new URLSearchParams(window.location.search).get("loginId");
  const prefilledLoginId = urlLoginId || user?.loginId;

  useEffect(() => {
    if (prefilledLoginId) {
      form.setFieldsValue({ loginId: prefilledLoginId });
    }
  }, [prefilledLoginId, form]);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 50) return t("login.passwordStrength.weak");
    if (strength < 75) return t("login.passwordStrength.medium");
    return t("login.passwordStrength.strong");
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 50) return "#ff4d4f";
    if (strength < 75) return "#faad14";
    return "#52c41a";
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setPasswordStrength(calculatePasswordStrength(password));
  };

  // 自定义校验规则：确认密码是否与新密码一致
  const validateConfirmPassword = async (_, value) => {
    if (!value) {
      return Promise.reject(new Error(t('login.inputNewPassword')));
    }
    const newPassword = form.getFieldValue("newPassword");
    if (value !== newPassword) {
      return Promise.reject(new Error(t('login.passwordNotMatch')));
    }
    return Promise.resolve();
  };


  const onFinish = async (values) => {
    setLoading(true);

    const { loginId, oldPassword, newPassword, password2, code } = values;
    try {
      const success = await resetPassword(loginId, oldPassword, newPassword, password2, code);
      if (success) {
        message.success(t("login.loginSuccess"));

        navigate("/login");
        // // 判断是否有重定向链接
        // const params = new URLSearchParams(window.location.search);
        // const redirect = params.get("redirectUrl");

        // if (redirect) {
        //   navigate(decodeURIComponent(redirect));
        // } else {
        //   navigate("/");
        // }
      } else {
        message.error(t("login.loginFailed"));
      }
    } catch (error) {
      console.error(t("login.loginError"), error);
      if (error.errorCode === "SC-A4210") {
        message.error(t("login.oldPasswordIncorrect"));
      } else {
        message.error(error.message || error.errorMessage || t("login.loginError"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* 左侧背景区域 */}
      <div className="login-left">
        <div className="login-left-content">
          <div className="login-logo">
            <span className="logo-text">LOTUS's</span>
          </div>
        </div>
      </div>

      {/* 右侧表单区域 */}
      <div className="login-right">
        <div className="login-header">
          <div className="login-brand">
            <span className="brand-text">LOTUS's</span>
          </div>
        </div>

        <div className="login-form-wrapper">
          <h1 className="login-title">{t('login.passwordReset')}</h1>
          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item
              name="loginId"
              label={t("login.username")}
              rules={[{ required: true, message: t("login.usernameRequired") }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t("login.username")}
                readOnly={!!prefilledLoginId}
              />
            </Form.Item>

            <Form.Item
              name="oldPassword"
              label={t("login.inputOldPassword")}
              rules={[{ required: true, message: t("login.passwordRequired") }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t("login.inputOldPassword")}
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label={t("login.inputNewPassword")}
              rules={[{ required: true, message: t("login.passwordRequired") }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t("login.inputNewPassword")}
                onChange={handlePasswordChange}
              />
            </Form.Item>

            <Form.Item
              name="password2"
              label={t("login.confirmNewPassword")}
              rules={[
                { required: true, message: t("login.confirmNewPassword") },
                { validator: validateConfirmPassword },
              ]}
            >
              <Input.Password
                prefix={<LockFilled />}
                placeholder={t("login.confirmNewPassword")}
              />
            </Form.Item>

            {passwordStrength > 0 && (
              <div className="password-strength">
                <div
                  style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
                >
                  {getPasswordStrengthText(passwordStrength)}
                </div>
                <Progress
                  percent={passwordStrength}
                  showInfo={false}
                  strokeColor={getPasswordStrengthColor(passwordStrength)}
                  size="small"
                />
              </div>
            )}

            <Form.Item>
              <a style={{ float: "right" }} href="/login">
                {t('login.goToLogin')}
              </a>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ width: "100%" }}
              >
                {t('login.passwordReset')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ResetPwdPage;

function resetPassword(loginId, oldPwd, newPwd, password2, validCode) {
  if (String(newPwd).trim() !== String(password2).trim()) {
    return Promise.reject(new Error(t('login.passwordNotMatch')));
  }

  return authService.resetPassword(loginId, oldPwd, newPwd, password2, validCode);
}
